import {FC, memo, useState} from 'react';
import {Tooltip} from 'react-tooltip';
import {Box, Center, Flex, useToken} from '@chakra-ui/react';
import {Dot, ISvgMenuItem, Svg, SvgMenu} from '@progress-fe/ui-kit';
import {RFSvg} from '@progress-fe/rf-core';
import Latex from 'react-latex-next';
import {EActionType} from 'core/enums';

import {IStructureItem} from '../../StructureItem';

const TOOLTIP_ID = 'STRUCTURE_TOOLTIP_ID';

interface IProps {
  item: IStructureItem;
  isChild?: boolean;
  isChildShown?: boolean;
  hasChildItems?: boolean;
  menuItems?: ISvgMenuItem<EActionType>[];
  onMenuAction?: (value: EActionType) => void;
  onToggleChildren?: () => void;
  onClick?: (uuidOrSubUuid: string) => void;
}

const ItemOrChildFC: FC<IProps> = ({
  item,
  isChild,
  menuItems,
  isChildShown,
  hasChildItems,
  onToggleChildren,
  onMenuAction,
  onClick
}) => {
  const [isMenuOpened, setIsMenuOpened] = useState(false);
  const [borderHex] = useToken('colors', ['border']);

  return (
    <Flex
      h="30px"
      gap="5px"
      role="group"
      alignItems="center"
      justify="space-between"
      p={`0px 5px 0px ${isChild ? '38px' : '8px'}`}
      _hover={{bg: item.isActive ? 'accent' : 'accentMid', cursor: 'pointer'}}
      {...(item.isActive ? {bg: 'accent', color: 'white'} : {color: 'black'})}
      data-tooltip-id={`${TOOLTIP_ID}_${item.uuid}`}
      onClick={() => onClick?.(item.uuid)}
    >
      <Tooltip
        id={`${TOOLTIP_ID}_${item.uuid}`}
        delayShow={250}
        place={'right-end'}
        border={`1px solid ${borderHex}`}
        closeEvents={{click: true, mouseleave: true}}
        style={{padding: '0', backgroundColor: 'white', zIndex: 10}}
        opacity="1"
      >
        <Box color="bodyText" p="6px 10px" fontSize="12px">
          <Latex>{item.name}</Latex>
        </Box>
      </Tooltip>

      <Flex gap="5px">
        {!!hasChildItems && (
          <Flex sx={{transform: isChildShown ? 'rotate(270deg)' : 'rotate(180deg)'}}>
            <Svg size={'s12'} name={'Back'} onClick={onToggleChildren} />
          </Flex>
        )}
        {!!item.icon && <Svg name={item.icon} />}
        {!!item.elementIcon && <RFSvg name={item.elementIcon} />}
        <Box maxW={!!item.suffix && !isChild ? '100px' : '200px'} className="cut-text">
          <Latex>{item.name}</Latex>
        </Box>
      </Flex>
      <Flex gap="5px">
        {!!item.suffix && !isChild && (
          <Box
            pt={'1px'}
            maxW={'115px'}
            textAlign="right"
            color={item.isActive ? 'accentMid' : 'accent'}
            className="cut-text"
          >
            {item.suffix}
          </Box>
        )}
        <Box w={'20px'} h={'20px'}>
          {!!menuItems && (
            <Box display={isMenuOpened ? 'block' : 'none'} _groupHover={{display: 'block'}}>
              <SvgMenu
                menuSize={'small'}
                menuIcon={'More'}
                items={menuItems}
                variant={'compactGray'}
                placement={'bottom-end'}
                onSelect={(value) => onMenuAction?.(value)}
                onMenuOpened={setIsMenuOpened}
              />
            </Box>
          )}

          {item.isError && (
            <Center w={'20px'} h={'100%'} _groupHover={{display: 'none'}}>
              <Dot variant={'error'} />
            </Center>
          )}
        </Box>
      </Flex>
    </Flex>
  );
};

export const ItemOrChild = memo(ItemOrChildFC);
